import React from 'react';
import { Alert, Modal, Spinner } from 'react-bootstrap';
import { ExternalLink } from 'lucide-react';
import { getBuildComponentTypeTranslation } from '../../Helpers/BuildComponentTypesStore';
import { useUsersData } from '../../Helpers/UsersContext';
import { ArchivedComponent, BuildingChanges, useArchiveDayChanges } from '../../hooks/archive/useArchiveChangeLog';
import { archivedFieldText, changeRowsOf, componentLinkOf, formatArchiveDate, formatChangeValue } from './archiveDateIndex';

interface ArchiveDayModalProps {
    date: string | null;
    onHide: () => void;
}

const typeName = (component: ArchivedComponent): string | undefined => {
    const specificType = archivedFieldText(component.specificType);
    return specificType ? getBuildComponentTypeTranslation(specificType, component.type) : undefined;
};

const componentDisplayName = (component: ArchivedComponent): string =>
    archivedFieldText(component.label) ?? typeName(component) ?? component.type;

/** "14:32" from a stored `YYYY-MM-DD hh:mm:ss` */
const timeOf = (archivedAt: string): string => archivedAt.split(' ')[1]?.slice(0, 5) ?? '';

const BuildingChangesTable: React.FC<{
    building: BuildingChanges;
    userName: (uid: string | null) => string;
}> = ({ building, userName }) => (
    <table className="archive-changes-table">
        <thead>
            <tr>
                <th>Komponent</th>
                <th>Fält</th>
                <th>Före</th>
                <th>Efter</th>
                <th>Av</th>
            </tr>
        </thead>
        {building.components.map(component => {
            const rows = changeRowsOf(component);
            const name = componentDisplayName(component);
            const type = typeName(component);
            const componentCell = (
                <td className="archive-change-component" rowSpan={Math.max(rows.length, 1)}>
                    <a
                        className="archive-change-component-name"
                        href={componentLinkOf(component)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={component._archiveMetadata.deleted
                            ? 'Öppna komponentens historik i ett nytt fönster'
                            : 'Öppna komponenten i ett nytt fönster'}
                    >
                        {name}
                        <ExternalLink size={12} aria-hidden="true" />
                    </a>
                    <div className="archive-change-component-meta">
                        {type && type !== name ? `${type} · ` : ''}senast {timeOf(component._archiveMetadata.archivedAt)}
                    </div>
                </td>
            );

            return (
                <tbody key={component._archiveMetadata.componentKey}>
                    {rows.length === 0 ? (
                        <tr>
                            {componentCell}
                            <td colSpan={4} className="archive-change-none">Inga ändringar i visade fält</td>
                        </tr>
                    ) : rows.map((row, index) => (
                        <tr key={row.field}>
                            {index === 0 && componentCell}
                            <td className="archive-change-field">{row.label}</td>
                            <td className="archive-change-old">
                                <span className="archive-change-cell-label">Före</span>
                                {formatChangeValue(row.field, row.oldValue, building.rooms)}
                            </td>
                            <td className="archive-change-new">
                                <span className="archive-change-cell-label">Efter</span>
                                {formatChangeValue(row.field, row.newValue, building.rooms)}
                            </td>
                            <td className="archive-change-by">
                                <span className="archive-change-cell-label">Av</span>
                                {userName(row.changedBy)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            );
        })}
    </table>
);

const ArchiveDayModal: React.FC<ArchiveDayModalProps> = ({ date, onHide }) => {
    const { changes, loading, error } = useArchiveDayChanges(date);
    const { users } = useUsersData();
    const componentCount = changes.reduce((sum, b) => sum + b.components.length, 0);

    const userName = (uid: string | null): string => {
        if (!uid) return '—';
        return users?.find(u => u.key === uid)?.name || 'Okänd användare';
    };

    return (
        <Modal show={date !== null} onHide={onHide} size="xl" scrollable className="archive-day-modal">
            <Modal.Header closeButton>
                <Modal.Title>
                    Ändringar {date ? formatArchiveDate(date, 'long') : ''}
                    {!loading && componentCount > 0 && (
                        <div className="archive-modal-subtitle">
                            {componentCount} komponenter i {changes.length} byggnader.
                            Före är värdet vid förra ändringsdagen.
                        </div>
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading && (
                    <div className="archive-loading">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Laddar ändringar...
                    </div>
                )}

                {error && <Alert variant="danger">{error}</Alert>}

                {!loading && !error && changes.length === 0 && (
                    <p className="archive-empty">Inga komponenter ändrades på detta datum</p>
                )}

                {!loading && changes.map(building => (
                    <section key={building.buildingKey} className="building-section">
                        <div className="building-header">
                            <h2>{building.buildingName}</h2>
                            <span className="component-count">{building.components.length} komponenter</span>
                        </div>
                        <BuildingChangesTable building={building} userName={userName} />
                    </section>
                ))}
            </Modal.Body>
        </Modal>
    );
};

export default ArchiveDayModal;
